import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SectionTitle } from "@/components/dashboard/widgets";
import {
  Webhook, ShieldCheck, Loader2, CheckCircle2, AlertCircle, Send,
  Power, Filter, Gauge, Clock, Lock, Copy, Activity, Code2, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import WebhookHealth from "@/components/dashboard/WebhookHealth";
import WebhookSecret from "@/components/dashboard/WebhookSecret";

const SECTOR_PRESETS = [
  { slug: "mortgages",           name: "Mortgages" },
  { slug: "business-finance",    name: "Business Finance" },
  { slug: "life-insurance",      name: "Life Insurance" },
  { slug: "wealth-management",   name: "Wealth Management" },
  { slug: "pensions",            name: "Pensions" },
  { slug: "debt-management",     name: "Debt Management" },
  { slug: "payday-loans",        name: "Payday Loans" },
  { slug: "income-protection",   name: "Income Protection" },
  { slug: "private-health",      name: "Private Health" },
  { slug: "car-finance",         name: "Car Finance" },
];

/**
 * /dashboard/delivery — Buyer self-service delivery config (iter 57).
 *
 * Sections:
 *   1. Inbound endpoint — webhook URL + auth header + timeout
 *   2. Test delivery — fires a sample lead, shows live response
 *   3. Active sectors — multi-select (matches /admin/buyers shape)
 *   4. Live toggle + daily cap
 *   5. Recent delivery log
 *   6. Payload spec sidebar
 */
export default function Delivery() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [logRows, setLogRows] = useState([]);
  const [last, setLast] = useState(null);

  // Form state
  const [webhook, setWebhook] = useState("");
  const [auth, setAuth] = useState("");
  const [timeoutMs, setTimeoutMs] = useState(5000);
  const [sectors, setSectors] = useState([]);
  const [live, setLive] = useState(false);
  const [dailyCap, setDailyCap] = useState(0);

  // iter61: CRM preset application — fills the auth header template based on the
  // selected CRM so buyers don't have to remember header-name conventions.
  const applyPreset = (preset) => {
    const p = CRM_PRESETS[preset];
    if (!p) return;
    if (!webhook) setWebhook(p.url_hint || "");
    setAuth(p.auth_template || "");
    toast.success(`${p.label} preset applied — paste your token in the auth header`);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/client/delivery");
        const c = data?.config || {};
        setWebhook(c.webhook_url || "");
        setAuth(c.auth_header || "");
        setTimeoutMs(c.timeout_ms || 5000);
        setSectors(c.sectors || []);
        setLive(!!c.live);
        setDailyCap(c.daily_cap || 0);
        setLast(data?.last_delivery || null);
        // Also fetch a recent log
        const log = await api.get("/client/delivery/log");
        setLogRows(log.data || []);
      } catch (e) {
        toast.error("Failed to load delivery settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/client/delivery", {
        webhook_url: webhook,
        auth_header: auth,
        timeout_ms: parseInt(timeoutMs, 10) || 5000,
        sectors,
        live,
        daily_cap: parseInt(dailyCap, 10) || 0,
      });
      toast.success("Delivery settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    if (!webhook) { toast.error("Add a webhook URL first"); return; }
    // Save first (so the server has the latest URL)
    await save();
    setTesting(true);
    try {
      const { data } = await api.post("/client/delivery/test", {});
      setLast(data);
      const log = await api.get("/client/delivery/log");
      setLogRows(log.data || []);
      if (data.ok) toast.success(`Delivered in ${data.duration_ms}ms · HTTP ${data.status_code}`);
      else toast.error(`Test failed: HTTP ${data.status_code} · ${data.error || ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const toggleSector = (slug) => {
    setSectors((s) => s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="size-5 animate-spin mr-2" />Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <SectionTitle title="Lead delivery" subtitle="Configure where Connexis sends your leads. All settings save instantly — only Live toggle controls inflow." />

      {/* Webhook health dashboard — at-a-glance success %, latency, status codes, failures */}
      <WebhookHealth />

      {/* HMAC signing secret + verifier snippet (P2 — webhook reliability layer) */}
      <WebhookSecret />

      <div className="grid lg:grid-cols-3 gap-5">
        {/* ============ LEFT: form (2 cols) ============ */}
        <div className="lg:col-span-2 space-y-5">
          {/* Inbound endpoint */}
          <Card className="rounded-xl border-slate-200">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Webhook className="size-5 text-[#0055FF]" strokeWidth={1.6} />
                <div className="flex-1">
                  <div className="text-[15px] font-semibold text-slate-900">Inbound endpoint</div>
                  <div className="text-[12.5px] text-slate-500">Connexis will POST a JSON lead to this URL the instant a match is found.</div>
                </div>
              </div>

              {/* iter61: CRM preset bar */}
              <div className="flex items-center gap-2 flex-wrap text-[12px]">
                <span className="text-slate-500">Connect a CRM:</span>
                {Object.entries(CRM_PRESETS).map(([k, p]) => (
                  <button
                    key={k}
                    onClick={() => applyPreset(k)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-colors text-slate-700"
                    data-testid={`crm-preset-${k}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div>
                <Label className="text-[12px] text-slate-700">Webhook URL *</Label>
                <Input
                  value={webhook} onChange={(e) => setWebhook(e.target.value)}
                  placeholder="https://your-crm.com/leads/inbound"
                  className="mt-1 font-mono text-[13px]"
                  data-testid="delivery-webhook-input"
                />
              </div>
              <div>
                <Label className="text-[12px] text-slate-700 flex items-center gap-1.5">
                  <Lock className="size-3" /> Auth header (optional)
                </Label>
                <Input
                  value={auth} onChange={(e) => setAuth(e.target.value)}
                  placeholder='Authorization: Bearer xxxx  ·  or  X-Api-Key: xxxx'
                  className="mt-1 font-mono text-[13px]"
                  data-testid="delivery-auth-input"
                />
                <p className="mt-1 text-[11.5px] text-slate-500">Either "Bearer …" (sets Authorization header) or "HeaderName: value" for custom headers.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[12px] text-slate-700 flex items-center gap-1.5"><Clock className="size-3" /> Timeout (ms)</Label>
                  <Input type="number" min={500} max={30000} step={500} value={timeoutMs}
                         onChange={(e) => setTimeoutMs(e.target.value)} className="mt-1" data-testid="delivery-timeout-input" />
                </div>
                <div>
                  <Label className="text-[12px] text-slate-700 flex items-center gap-1.5"><Gauge className="size-3" /> Daily cap (leads)</Label>
                  <Input type="number" min={0} step={5} value={dailyCap} placeholder="0 = unlimited"
                         onChange={(e) => setDailyCap(e.target.value)} className="mt-1" data-testid="delivery-cap-input" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button onClick={test} disabled={testing || !webhook} variant="outline" className="rounded-full" data-testid="delivery-test-btn">
                  {testing ? <><Loader2 className="size-3.5 animate-spin" /> Testing…</> : <><Send className="size-3.5" /> Test delivery</>}
                </Button>
                <Button onClick={save} disabled={saving} className="rounded-full" data-testid="delivery-save-btn">
                  {saving ? <><Loader2 className="size-3.5 animate-spin" /> Saving…</> : "Save changes"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Sectors */}
          <Card className="rounded-xl border-slate-200">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center gap-2">
                <Filter className="size-5 text-[#0055FF]" strokeWidth={1.6} />
                <div>
                  <div className="text-[15px] font-semibold text-slate-900">Active sectors</div>
                  <div className="text-[12.5px] text-slate-500">Only leads matching at least one selected sector will be routed to you.</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {SECTOR_PRESETS.map((s) => (
                  <button
                    key={s.slug}
                    onClick={() => toggleSector(s.slug)}
                    className={`text-left rounded-lg border px-3 py-2.5 text-[13px] transition-all flex items-center justify-between ${
                      sectors.includes(s.slug)
                        ? "border-[#0055FF] bg-[#0055FF]/[0.04] text-slate-900 ring-1 ring-[#0055FF]/15"
                        : "border-slate-200 text-slate-700 hover:border-slate-300"
                    }`}
                    data-testid={`delivery-sector-${s.slug}`}
                  >
                    <span>{s.name}</span>
                    {sectors.includes(s.slug) && <CheckCircle2 className="size-3.5 text-[#0055FF]" />}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Live toggle */}
          <Card className="rounded-xl border-slate-200">
            <CardContent className="p-6 flex items-center justify-between gap-5">
              <div className="flex items-center gap-3">
                <Power className={`size-5 ${live ? "text-emerald-600" : "text-slate-400"}`} strokeWidth={1.7} />
                <div>
                  <div className="text-[15px] font-semibold text-slate-900">
                    Delivery is {live ? <span className="text-emerald-600">LIVE</span> : <span className="text-slate-500">PAUSED</span>}
                  </div>
                  <div className="text-[12.5px] text-slate-500">
                    {live ? "We're sending leads to your endpoint in real-time." : "No leads will be routed until you turn delivery on."}
                  </div>
                </div>
              </div>
              <Switch checked={live} onCheckedChange={(v) => { setLive(v); setTimeout(save, 100); }} data-testid="delivery-live-toggle" />
            </CardContent>
          </Card>

          {/* Recent log */}
          <Card className="rounded-xl border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="size-5 text-[#0055FF]" strokeWidth={1.6} />
                <div>
                  <div className="text-[15px] font-semibold text-slate-900">Recent delivery attempts</div>
                  <div className="text-[12.5px] text-slate-500">Last 25 test sends and live deliveries.</div>
                </div>
              </div>
              {logRows.length === 0 ? (
                <div className="rounded-lg bg-slate-50 px-4 py-8 text-center text-[13px] text-slate-500">
                  Run a test delivery to see logs here.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {logRows.map((r, i) => (
                    <div key={i} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-b-0 text-[12.5px]">
                      {r.ok ? <CheckCircle2 className="size-4 text-emerald-500 shrink-0" /> : <AlertCircle className="size-4 text-rose-500 shrink-0" />}
                      <span className="font-mono text-slate-500 tabular-nums w-32 shrink-0">{new Date(r.at).toLocaleString("en-GB")}</span>
                      <span className={`tabular-nums font-mono w-14 ${r.ok ? "text-slate-700" : "text-rose-600"}`}>HTTP {r.status_code || "—"}</span>
                      <span className="text-slate-500 tabular-nums w-16">{r.duration_ms}ms</span>
                      {r.test && <span className="text-[10px] uppercase tracking-widest font-semibold text-amber-700 bg-amber-100 rounded px-1.5">test</span>}
                      <span className="text-slate-500 truncate flex-1">{r.response_snippet || r.error || "—"}</span>
                      {!r.ok && (
                        <button
                          onClick={test}
                          disabled={testing}
                          className="text-[11px] text-[#0055FF] hover:underline shrink-0"
                          data-testid={`delivery-resend-${i}`}
                        >
                          Resend
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ============ RIGHT: payload spec sidebar ============ */}
        <div className="space-y-5">
          {/* Last test result */}
          {last && (
            <Card className={`rounded-xl border-2 ${last.ok ? "border-emerald-300 bg-emerald-50/40" : "border-rose-300 bg-rose-50/40"}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  {last.ok
                    ? <CheckCircle2 className="size-5 text-emerald-600" />
                    : <AlertCircle className="size-5 text-rose-600" />}
                  <div className="text-[14px] font-semibold text-slate-900">
                    {last.ok ? "Last test succeeded" : "Last test failed"}
                  </div>
                </div>
                <div className="text-[12px] text-slate-700 space-y-0.5 font-mono tabular-nums">
                  <div>HTTP {last.status_code || "—"} · {last.duration_ms}ms</div>
                  <div className="text-slate-500 truncate">{last.response_snippet || last.error || ""}</div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payload spec */}
          <Card className="rounded-xl border-slate-200 bg-slate-900 text-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="size-4 text-[#60A5FA]" strokeWidth={1.6} />
                <div className="text-[12px] font-semibold uppercase tracking-widest text-slate-300">Payload spec</div>
                <button
                  className="ml-auto text-slate-400 hover:text-white"
                  onClick={() => { navigator.clipboard.writeText(SAMPLE_JSON); toast.success("Copied payload"); }}
                  data-testid="delivery-copy-payload"
                >
                  <Copy className="size-3.5" />
                </button>
              </div>
              <pre className="text-[10.5px] leading-relaxed overflow-x-auto font-mono whitespace-pre tabular-nums">{SAMPLE_JSON}</pre>
              <div className="mt-3 text-[11px] text-slate-400">
                We expect a <span className="text-emerald-400 font-mono">{"200 OK"}</span> response within your timeout. Body may contain <span className="text-emerald-400 font-mono">{"{ \"accept\": true }"}</span> to confirm receipt.
              </div>
            </CardContent>
          </Card>

          {/* Postback URL (closes-the-loop) */}
          <Card className="rounded-xl border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="size-4 text-[#0055FF]" strokeWidth={1.7} />
                <div className="text-[13px] font-semibold text-slate-900">Disposition postback</div>
              </div>
              <p className="text-[12px] text-slate-600 mb-3">
                Tell us how each lead converted in your CRM (sold / junk / unreachable). We tune the ping-tree against your real outcomes.
              </p>
              <pre className="text-[10.5px] leading-relaxed overflow-x-auto font-mono whitespace-pre bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-800">{POSTBACK_SAMPLE}</pre>
              <button
                className="mt-3 text-[12px] text-[#0055FF] hover:underline inline-flex items-center gap-1"
                onClick={() => { navigator.clipboard.writeText(POSTBACK_SAMPLE); toast.success("Copied postback curl"); }}
                data-testid="delivery-copy-postback"
              >
                <Copy className="size-3" /> Copy
              </button>
              <p className="mt-3 text-[11px] text-slate-500">
                Each delivered lead carries a unique <code className="font-mono">postback_token</code> in its payload — use that token as the auth header above.
              </p>
            </CardContent>
          </Card>

          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-[13px] font-semibold text-slate-900 mb-1.5">Need a CRM integration instead?</div>
            <div className="text-[12.5px] text-slate-600 mb-3">HubSpot, Salesforce, Pipedrive native connectors are available as a one-time £99 add-on.</div>
            <a href="/contact" className="text-[12.5px] text-[#0055FF] hover:underline">Talk to our team →</a>
          </div>

          {/* Iter97: White-label embeddable buyer report */}
          <EmbedReportCard />
        </div>
      </div>
    </div>
  );
}

const SAMPLE_JSON = `{
  "lead_id":    "lead_01HV...",
  "sector":     "mortgages",
  "first_name": "Sarah",
  "last_name":  "O'Connell",
  "email":      "sarah@example.co.uk",
  "phone":      "+447712345678",
  "postcode":   "M2 1AA",
  "consent": {
    "method":    "TrustedForm",
    "timestamp": "2026-02-18T14:22:11Z",
    "ip":        "82.21.44.190"
  },
  "campaign_id":     "cmp_47x...",
  "price_gbp":       38.00,
  "delivered_at":    "2026-02-18T14:22:12Z",
  "postback_token":  "pt_kJ8...rE2"
}`;

const POSTBACK_SAMPLE = `curl -X POST https://connex.is/api/v1/leads/{lead_id}/postback \\
  -H "X-Postback-Token: <token from delivered lead>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "status": "sold",
    "sale_value_gbp": 1200,
    "notes": "closed at 4.6%"
  }'`;

// iter61 — CRM webhook presets. Buyers click one and we fill the auth header pattern.
// We deliberately *don't* OAuth — these are pragmatic webhook templates that work today
// against any HubSpot/Salesforce/Pipedrive workflow trigger.
const CRM_PRESETS = {
  hubspot: {
    label: "HubSpot",
    url_hint: "https://api.hsforms.com/submissions/v3/integration/submit/<portal-id>/<form-guid>",
    auth_template: "Authorization: Bearer <YOUR_HUBSPOT_PRIVATE_APP_TOKEN>",
  },
  salesforce: {
    label: "Salesforce",
    url_hint: "https://<instance>.my.salesforce.com/services/apexrest/leads/inbound",
    auth_template: "Authorization: Bearer <YOUR_SALESFORCE_SESSION_ID>",
  },
  pipedrive: {
    label: "Pipedrive",
    url_hint: "https://api.pipedrive.com/v1/leads?api_token=<YOUR_API_TOKEN>",
    auth_template: "X-API-Token: <YOUR_PIPEDRIVE_TOKEN>",
  },
  zapier: {
    label: "Zapier",
    url_hint: "https://hooks.zapier.com/hooks/catch/<account-id>/<hook-id>/",
    auth_template: "",
  },
  custom: {
    label: "Custom",
    url_hint: "https://your-crm.example.com/leads/inbound",
    auth_template: "Authorization: Bearer <YOUR_TOKEN>",
  },
};


/* ─────────────────────────  Iter97 — White-label report iframe  ───────────────────────── */

function EmbedReportCard() {
  const [minting, setMinting] = useState(false);
  const [mint, setMint] = useState(null);
  const [copied, setCopied] = useState(null);

  const handleMint = async () => {
    setMinting(true);
    try {
      const { data } = await api.post("/client/embed-tokens", {});
      setMint(data);
      toast.success("Embed link minted — valid for 1 year");
    } catch (e) {
      toast.error("Could not mint embed link");
    } finally {
      setMinting(false);
    }
  };

  const copy = async (label, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`Copied ${label}`);
      setTimeout(() => setCopied(null), 1600);
    } catch (_) {
      toast.error("Clipboard blocked");
    }
  };

  return (
    <Card className="rounded-xl border-slate-200" data-testid="embed-report-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Code2 className="size-4 text-violet-500" strokeWidth={1.7} />
          <div className="text-[13px] font-semibold text-slate-900">Embed your report</div>
        </div>
        <p className="text-[12px] text-slate-600 mb-3">
          Drop a live Connexis report into HubSpot, Notion, your internal dashboard — any iframe-friendly surface. Token-scoped to your account, no login required.
        </p>

        {!mint && (
          <Button
            onClick={handleMint}
            disabled={minting}
            className="bg-slate-900 hover:bg-slate-800 gap-1.5"
            data-testid="embed-mint-btn"
          >
            {minting ? <Loader2 className="size-3.5 animate-spin" /> : <Code2 className="size-3.5" />}
            Generate embed link
          </Button>
        )}

        {mint && (
          <div className="space-y-3" data-testid="embed-mint-result">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Embed URL</Label>
                <a href={mint.iframe_url} target="_blank" rel="noopener noreferrer" className="text-[11.5px] text-[#0055FF] hover:underline inline-flex items-center gap-1" data-testid="embed-preview-link">
                  Preview <ExternalLink className="size-3" />
                </a>
              </div>
              <div className="flex gap-2">
                <Input value={mint.iframe_url} readOnly className="font-mono text-[11.5px]" data-testid="embed-url-input" />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copy("URL", mint.iframe_url)}
                  className="border-slate-200 shrink-0"
                  data-testid="embed-copy-url"
                >
                  {copied === "URL" ? <CheckCircle2 className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Paste-ready iframe</Label>
              <div className="relative mt-1 rounded-md border border-slate-800 bg-slate-950 overflow-hidden">
                <button
                  onClick={() => copy("HTML", mint.embed_html)}
                  className="absolute top-2.5 right-2.5 inline-flex items-center gap-1.5 rounded h-7 px-2.5 bg-slate-800/80 hover:bg-slate-700 text-[11px] font-semibold text-slate-100 z-10"
                  data-testid="embed-copy-html"
                >
                  {copied === "HTML" ? <CheckCircle2 className="size-3 text-emerald-400" /> : <Copy className="size-3" />}
                  {copied === "HTML" ? "Copied" : "Copy"}
                </button>
                <pre className="font-mono text-[11px] leading-[1.55] text-slate-100 p-3 pr-20 overflow-x-auto whitespace-pre">{mint.embed_html}</pre>
              </div>
            </div>

            <div className="text-[11px] text-slate-500 flex items-center justify-between">
              <span>Expires {new Date(mint.expires_at).toLocaleDateString()}</span>
              <button onClick={handleMint} className="text-[#0055FF] hover:underline" data-testid="embed-remint-btn">Re-mint (revokes old link)</button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
